import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import PurpleArrowDown from "../img/PurpleArrowDown.svg";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance";
import Add_round from "../img/Add_round.svg";
import Minus from "../img/Minus.svg";

const MyProducts = () => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(null); // Store dropdown state per product
  const [products, setProducts] = useState([]); // State to store the products data
  const [loading, setLoading] = useState(true);
  const [topUpPlans, setTopUpPlans] = useState({}); // To store top-up plans for each product
  const [quantities, setQuantities] = useState({}); // To store the quantities for each top-up
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [lastClickedDropdown, setLastClickedDropdown] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
        setLastClickedDropdown(showDropdown); // remember what was closed
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const toggleDropdown = async (productId, itemId) => {
    if (lastClickedDropdown === productId && showDropdown === null) {
      // It was just closed via outside click, so do nothing
      setLastClickedDropdown(null);
      return;
    }
    // Toggle the dropdown for the clicked product
    if (showDropdown === productId) {
      setShowDropdown(null); // Close if same product is clicked
    } else {
      setShowDropdown(productId);

      // Fetch top-up plans if not already fetched for this product
      if (!topUpPlans[productId]) {
        try {
          const accessToken = localStorage.getItem("accessToken");

          if (!accessToken) {
            alert("Please log in first.");
            return;
          }

          // Pass the specific item_id (for each product) to the API
          const response = await api.get(
            `/display/api/v1/list-top-ups-in-my-product/${itemId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.data.status === "success") {
            setTopUpPlans((prevState) => ({
              ...prevState,
              [productId]: response.data.data, // Store the top-up data for the product
            }));
          } else {
            console.error(
              "Failed to fetch top-up plans:",
              response.data.response_code
            );
          }
        } catch (error) {
          console.error("Error fetching top-up plans:", error);
        }
      }
    }
  };

  const handleQuantityChange = (topupId, change) => {
    setQuantities((prevState) => {
      const currentQuantity = prevState[topupId] || 0;
      const newQuantity = Math.max(0, currentQuantity + change); // Ensure quantity doesn't go below 0
      return { ...prevState, [topupId]: newQuantity };
    });
  };

  const calculateTotal = (productId) => {
    if (!topUpPlans[productId]) return 0;

    return topUpPlans[productId].reduce((total, topup) => {
      const quantity = quantities[topup.top_up_id] || 0;
      return total + topup.price * quantity;
    }, 0);
  };

  const fetchProducts = async () => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      alert("Please log in first.");
      return;
    }

    try {
      const response = await api.get("/display/api/v1/my-products", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data.status === "success") {
        setProducts(response.data.data); // Set the products data in state
      } else {
        console.error("Failed to fetch products:", response.data.response_code);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false); // Set loading to false once the request completes
    }
  };

  const showSuccessModal = () => {
    setSuccessModalOpen(true);
  };

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
    navigate("/cart"); // replace "/nextPage" with your actual route
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const handleAddToCart = async (productId) => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      alert("Please log in first.");
      return;
    }

    const selectedTopUps = (topUpPlans[productId] || [])
      .filter((topUp) => quantities[topUp.top_up_id] > 0)
      .map((topUp) => ({
        topup_id: topUp.top_up_id,
        quantity: quantities[topUp.top_up_id],
        price: topUp.price,
      }));

    if (selectedTopUps.length === 0) {
      showModal("Please select at least one top-up to add to cart.");
      return;
    }

    try {
      const response = await api.post(
        "/display/api/v1/add-to-cart-from-my-products",
        { line_data: selectedTopUps },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("API Response:", response); // ✅ Ensure this is present

      if (response.data.status === "success") {
        showSuccessModal();
      } else {
        showModal("Failed to add to cart: " + response.data.response_code);
      }
    } catch (error) {
      console.error("Error adding to cart:", error); // ✅ Ensure this is present
      alert("Something went wrong while adding to cart.");
    }
  };

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMessage("");
  };

  const getGroupedTopUps = (topUps) => {
    const grouped = {};

    topUps.forEach((topup) => {
      if (grouped[topup.topup_id]) {
        grouped[topup.topup_id].count += topup.count;
      } else {
        grouped[topup.topup_id] = { ...topup };
      }
    });

    return Object.values(grouped); // convert back to array
  };

  return (
    <div>
      <Header />
      <div
        className="px-5 sm:px-10 mb-5"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 my-10">
          <h1 className="text-3xl font-semibold">My Products</h1>

          {products.length > 0 && (
            <span
              className="px-4 h-8 flex text-sm items-center cursor-pointer rounded text-white font-semibold"
              style={{
                background: "linear-gradient(270deg, #9061DB 0%, #8777F3 100%)",
              }}
              onClick={() => navigate("/ViewInvoice")}
            >
              View Invoice
            </span>
          )}
        </div>

        {loading ? (
          <div>Loading...</div> // Show loading indicator while fetching data
        ) : products.length > 0 ? (
          products.map((product) => (
            <div
              key={product.id}
              className="border border-[#D9D9D9] p-4 hover hover:bg-[#F1EFFF] rounded-lg mt-4 w-full bg-white  cursor-pointer"
              style={{ boxShadow: "0px 1px 4px 0px #00000040" }}
              onClick={() =>
                navigate("/PurchasedProductSingleView", {
                  state: {
                    Id: product.id,
                    item_id: product.item_id,
                    itemType: product.item_type,
                    project_code: product.project_code,
                  },
                })
              }
            >
              <div className="w-full flex flex-col sm:flex-row sm:justify-between  gap-3">
                <div className="flex gap-2 ">
                  <div className="h-14 w-14">
                    <img src={product.app_icon} alt="" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm">{product.project_name}</span>
                    <span className="text-xs text-gray-500">
                      {product.company_name}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:w-[55%]">
                  <div className="flex flex-col sm:flex-row">
                    <div className="flex flex-col sm:justify-between px-3 w-full gap-2">
                      <span className="text-sm">{product.item_name}</span>
                      <span className="text-sm">
                        Expiry Date :{" "}
                        {new Date(product.ending_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div
                      className="flex w-full justify-start sm:justify-end mt-2 relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="flex justify-center items-center justify-between bg-[#CEC7FF4D] w-full max-w-[260px] gap-2 px-3 py-2 rounded text-[#9367D7] text-sm font-bold cursor-pointer overflow-hidden"
                        onClick={(e) => {
                          // Prevent the parent click event (to navigate) when clicking the dropdown
                          e.stopPropagation();
                          toggleDropdown(product.id, product.item_id);
                        }}
                      >
                        <span className="truncate max-w-[90px] block whitespace-nowrap">
                          Top Up Plan
                        </span>
                        <img
                          src={PurpleArrowDown}
                          alt="Dropdown"
                          className="cursor-pointer flex-shrink-0"
                        />
                      </div>

                      {/* Dropdown for item_id */}
                      {showDropdown === product.id && (
                        <div
                          ref={dropdownRef}
                          className="absolute top-10 z-50 bg-white border p-4 rounded-lg shadow-md w-72 sm:w-[16.5rem]"
                        >
                          <div className="space-y-2">
                            {topUpPlans[product.id]?.length === 0 ? (
                              <div className="text-sm text-gray-500 px-2 py-2">
                                No top-up available
                              </div>
                            ) : (
                              topUpPlans[product.id]?.map((topUpPlan) => (
                                <div
                                  key={topUpPlan.top_up_id}
                                  className="flex items-center justify-between gap-3 border p-1 rounded-lg"
                                >
                                  {/* Top-up Name and Price */}
                                  <div className="flex flex-col justify-between gap-1">
                                    <span className="text-sm">
                                      {topUpPlan.name}
                                    </span>
                                    <span className="text-xs">
                                      ₹{topUpPlan.price}/qty
                                    </span>
                                  </div>

                                  {/* Quantity Adjuster */}
                                  <div className="flex gap-2 items-center">
                                    <img
                                      src={Minus}
                                      alt="Decrease"
                                      className="cursor-pointer"
                                      onClick={() =>
                                        handleQuantityChange(
                                          topUpPlan.top_up_id,
                                          -1
                                        )
                                      }
                                    />
                                    <div className="w-7 h-6 bg-gray-300 flex items-center justify-center rounded-md">
                                      {quantities[topUpPlan.top_up_id] || 0}
                                    </div>
                                    <img
                                      src={Add_round}
                                      alt="Increase"
                                      className="cursor-pointer"
                                      onClick={() =>
                                        handleQuantityChange(
                                          topUpPlan.top_up_id,
                                          1
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Total Pay */}
                          {topUpPlans[product.id]?.length > 0 && (
                            <>
                              <div className="flex justify-between mt-3">
                                <span className="font-semibold text-xs">
                                  Total Top-Up Payment
                                </span>
                                <span className="sm">
                                  ₹{calculateTotal(product.id)}
                                </span>
                              </div>

                              <div className="w-full flex justify-end">
                                <button
                                  className="text-white text-sm font-bold p-2 w-1/2 rounded mt-2 mb-2 w-full"
                                  style={{
                                    background:
                                      "linear-gradient(270deg, #9061DB 0%, #8777F3 100%)",
                                  }}
                                  onClick={() => handleAddToCart(product.id)}
                                >
                                  Add to Cart
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex w-full justify-between items-center gap-1 relative  ">
                    {/* Display the count dynamically */}
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mt-2 w-full">
                      {getGroupedTopUps(product.top_ups).map((topup) => (
                        <div
                          key={topup.topup_id}
                          className="flex flex-col sm:flex-row justify-between items-start sm:items-center  px-3 py-5 rounded-md bg-[#CEC7FF1A] text-xs gap-1 sm:gap-4 w-full"
                        >
                          <span className="w-full sm:w-[55.5%]">
                            Top up: {topup.top_up_name}
                          </span>
                          <span className="w-[35.5%] ">
                            Count: {topup.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <span className="flex mt-6 text-base">No products added yet.</span> // Show this message when no products are available
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm text-center">
            <p className="text-gray-800 mb-4">{modalMessage}</p>
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-[#9061DB] text-white rounded hover:opacity-90 text-sm font-bold"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {successModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm text-center">
            <p className="text-gray-800 mb-4">
              Top-ups added to cart successfully!
            </p>
            <button
              onClick={closeSuccessModal}
              className="px-4 py-2 bg-[#9061DB] text-white rounded hover:opacity-90 text-sm font-bold"
            >
              Go to cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProducts;
